import React, {useRef, useState} from 'react';
import styled from 'styled-components';
import {validateEmail} from '../utils/validateEmail';
import {RecipientChip} from './RecipientChip';

interface Props {
	label: string;
	values: string[];
	onChange: (values: string[]) => void;
}

const FieldBox = styled.div`
	display: flex;
	flex-wrap: wrap;
	align-items: center;
	gap: 4px;
	border-radius: 8px;
	border: 1px solid #cbd5e1;
	background: #ffffff;
	padding: 6px 8px;
	&:focus-within {
		border-color: #3b82f6;
		box-shadow: 0 0 0 1px #3b82f6;
	}
`;

const FieldLabel = styled.span`
	flex-shrink: 0;
	font: ${p => p.theme.ticketing.font.subheading2};
	color: #64748b;
`;

const FieldInput = styled.input`
	min-width: 120px;
	flex: 1 1 0%;
	border: 0;
	background: transparent;
	font: ${p => p.theme.ticketing.font.subheading2};
	color: #334155;
	outline: none;
	&::placeholder {
		color: #94a3b8;
	}
`;

const ErrorText = styled.p`
	margin-top: 4px;
	font: ${p => p.theme.ticketing.font.body3};
	color: #dc2626;
`;

export function RecipientField({label, values, onChange}: Props) {
	const [inputValue, setInputValue] = useState('');
	const [error, setError] = useState<string | null>(null);
	const inputRef = useRef<HTMLInputElement>(null);

	function commitPending(raw: string) {
		const trimmed = raw.trim().replace(/,$/, '').trim();
		if (!trimmed) return;

		const normalized = trimmed.toLowerCase();

		if (!validateEmail(normalized)) {
			setError(`"${trimmed}" is not a valid email address`);
			return;
		}

		if (values.map(v => v.toLowerCase()).includes(normalized)) {
			setInputValue('');
			return;
		}

		onChange([...values, trimmed]);
		setInputValue('');
		setError(null);
	}

	function handleKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
		if (e.key === 'Enter' || e.key === 'Tab' || e.key === ',') {
			e.preventDefault();
			commitPending(inputValue);
			return;
		}

		if (e.key === 'Backspace' && inputValue === '' && values.length > 0) {
			onChange(values.slice(0, -1));
		}
	}

	function handleBlur() {
		if (inputValue.trim()) {
			commitPending(inputValue);
		}
	}

	function handleChange(e: React.ChangeEvent<HTMLInputElement>) {
		const val = e.target.value;
		if (val.endsWith(',')) {
			commitPending(val);
		} else {
			setInputValue(val);
			setError(null);
		}
	}

	function removeAt(index: number) {
		onChange(values.filter((_, i) => i !== index));
	}

	return (
		<div>
			<FieldBox onClick={() => inputRef.current?.focus()}>
				<FieldLabel>{label}:</FieldLabel>
				{values.map((email, i) => (
					<RecipientChip key={email} email={email} invalid={!validateEmail(email)} onRemove={() => removeAt(i)} />
				))}
				<FieldInput
					ref={inputRef}
					type="text"
					value={inputValue}
					aria-label={label}
					onChange={handleChange}
					onKeyDown={handleKeyDown}
					onBlur={handleBlur}
					placeholder={values.length === 0 ? 'name@example.com' : ''}
				/>
			</FieldBox>
			{error && <ErrorText role="alert">{error}</ErrorText>}
		</div>
	);
}
